import React, { useState, useEffect } from 'react';
import useFormDraft from '../hooks/useFormDraft';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Grid,
  Autocomplete,
  IconButton,
  Typography,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Article } from '../types';
import { Supplier } from '../types/supplier';
import { StockEntryNewCreate, StockEntryNew, StockEntryItemCreate } from '../types/stockEntryNew';
import { stockEntriesNewAPI } from '../services/api';
import { extractErrorMessage } from '../utils/errorHandler';
import ConfirmationDialog, { ConfirmationField } from './ConfirmationDialog';
import { SlideUpTransition, useMinimizable, DialogControls, MinimizedBar } from './MinimizableDialog';

interface StockEntryNewAddProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  articles: Article[];
  suppliers: Supplier[];
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

interface ArticleLineItem extends Omit<StockEntryItemCreate, 'unit_price' | 'quantity'> {
  article?: Article;
  quantity: number | null | string;
  unit_price: number | null | string;
}

const StockEntryNewAdd: React.FC<StockEntryNewAddProps> = ({
  open,
  onClose,
  onSuccess,
  articles,
  suppliers,
  showError,
  showSuccess,
  minimized: controlledMinimized,
  onMinimizedChange,
}) => {
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(
    open,
    controlledMinimized,
    onMinimizedChange
  );

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    bl_number: '',
    supplier_id: null as number | null,
  });

  const [articleLines, setArticleLines] = useState<ArticleLineItem[]>([
    {
      article_id: 0,
      quantity: null,
      unit_price: null,
    },
  ]);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [createdResponse, setCreatedResponse] = useState<StockEntryNew | null>(null);

  // Cache the in-progress form so an accidental backdrop-close doesn't wipe it.
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    formData: typeof formData;
    articleLines: ArticleLineItem[];
  }>('stock-entry');

  // Restore a cached draft when the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      const draft = loadDraft();
      if (draft) {
        setFormData(draft.formData);
        setArticleLines(draft.articleLines);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const formatNumber = (value: any, decimals: number = 2): string => {
    return Number(value || 0).toFixed(decimals);
  };

  const handleAddArticleLine = () => {
    setArticleLines([
      ...articleLines,
      {
        article_id: 0,
        quantity: null,
        unit_price: null,
      },
    ]);
  };

  const handleRemoveArticleLine = (index: number) => {
    if (articleLines.length > 1) {
      setArticleLines(articleLines.filter((_, i) => i !== index));
    }
  };

  const handleArticleChange = (index: number, article: Article | null) => {
    const newLines = [...articleLines];

    if (article) {
      newLines[index] = {
        ...newLines[index],
        article_id: article.id,
        article: article,
      };
    } else {
      newLines[index] = {
        article_id: 0,
        quantity: null,
        unit_price: null,
      };
    }

    setArticleLines(newLines);
  };

  const handleArticleFieldChange = (
    index: number,
    field: keyof ArticleLineItem,
    value: any
  ) => {
    const newLines = [...articleLines];
    newLines[index] = {
      ...newLines[index],
      [field]: value,
    };
    setArticleLines(newLines);
  };

  const calculateGrandTotal = (): number => {
    return articleLines.reduce((total, line) => {
      const quantity = typeof line.quantity === 'string'
        ? parseFloat(line.quantity.replace(',', '.')) || 0
        : (line.quantity ?? 0);
      const price = typeof line.unit_price === 'string'
        ? parseFloat(line.unit_price.replace(',', '.')) || 0
        : (line.unit_price ?? 0);
      const lineTotal = quantity * price;
      return total + lineTotal;
    }, 0);
  };

  const handleSubmitClick = () => {
    // Validate all required fields
    if (!formData.bl_number || !formData.bl_number.trim()) {
      showError('Veuillez saisir le numero de BL');
      return;
    }

    if (!formData.supplier_id) {
      showError('Veuillez selectionner un fournisseur');
      return;
    }

    // Validate article lines
    for (let i = 0; i < articleLines.length; i++) {
      const line = articleLines[i];

      if (!line.article_id || line.article_id === 0) {
        showError(`Veuillez selectionner un article pour la ligne ${i + 1}`);
        return;
      }

      const quantity = typeof line.quantity === 'string'
        ? parseFloat(line.quantity.replace(',', '.'))
        : line.quantity;

      if (quantity === null || isNaN(quantity as number) || (quantity as number) <= 0) {
        showError(`La quantite doit etre superieure a 0 pour la ligne ${i + 1}`);
        return;
      }

      const price = typeof line.unit_price === 'string'
        ? parseFloat(line.unit_price.replace(',', '.'))
        : line.unit_price;

      if (price === null || isNaN(price as number) || (price as number) < 0) {
        showError(`Le prix unitaire doit etre superieur ou egal a 0 pour la ligne ${i + 1}`);
        return;
      }
    }

    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);

    try {
      const createData: StockEntryNewCreate = {
        date: formData.date || new Date().toISOString().split('T')[0],
        bl_number: formData.bl_number,
        supplier_id: formData.supplier_id!,
        items: articleLines.map((line) => {
          const quantity = typeof line.quantity === 'string'
            ? parseFloat(line.quantity.replace(',', '.'))
            : (line.quantity ?? 0);
          const price = typeof line.unit_price === 'string'
            ? parseFloat(line.unit_price.replace(',', '.'))
            : (line.unit_price ?? 0);
          return {
            article_id: line.article_id,
            quantity: Math.round(quantity * 100) / 100,
            unit_price: Math.round(price * 100) / 100,
          };
        }),
      };

      const result = await stockEntriesNewAPI.create(createData);
      setCreatedResponse(result);
      setShowConfirmDialog(false);
      setShowSuccessDialog(true);
      showSuccess(
        `Entree de stock creee avec succes! ${result.total_articles} article(s), Total: ${result.grand_total} MAD`
      );
      clearDraft();
      onSuccess();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getConfirmationFields = (): ConfirmationField[] => {
    const fields: ConfirmationField[] = [];

    fields.push({ label: 'Date', value: formData.date });
    fields.push({ label: 'BL N', value: formData.bl_number });

    if (formData.supplier_id) {
      const supplier = suppliers.find((s) => s.id === formData.supplier_id);
      if (supplier) {
        fields.push({ label: 'Fournisseur', value: `${supplier.code} - ${supplier.name}` });
      }
    }

    fields.push({ label: '---', value: '---' });
    fields.push({ label: 'Articles', value: `${articleLines.length}`, bold: true });

    articleLines.forEach((line, index) => {
      const article = articles.find((a) => a.id === line.article_id);
      if (article) {
        const quantity = typeof line.quantity === 'string'
          ? parseFloat(line.quantity.replace(',', '.')) || 0
          : (line.quantity ?? 0);
        const price = typeof line.unit_price === 'string'
          ? parseFloat(line.unit_price.replace(',', '.')) || 0
          : (line.unit_price ?? 0);

        fields.push({
          label: `Article ${index + 1}`,
          value: `${article.reference} - ${article.designation}`,
        });
        fields.push({
          label: `  Quantite`,
          value: `${quantity}`,
        });
        fields.push({
          label: `  Prix Unitaire`,
          value: `${formatNumber(price)} MAD`,
        });
        fields.push({
          label: `  Total`,
          value: `${formatNumber(quantity * price)} MAD`,
        });
      }
    });

    fields.push({ label: '---', value: '---' });
    fields.push({
      label: 'TOTAL GENERAL',
      value: `${formatNumber(calculateGrandTotal())} MAD`,
      bold: true,
    });

    return fields;
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    setCreatedResponse(null);
    handleClose();
  };

  // Annuler = deliberate cancel: wipe inputs and discard any draft.
  const handleClose = () => {
    clearDraft();
    setFormData({
      date: new Date().toISOString().split('T')[0],
      bl_number: '',
      supplier_id: null,
    });
    setArticleLines([
      {
        article_id: 0,
        quantity: null,
        unit_price: null,
      },
    ]);
    setShowConfirmDialog(false);
    setIsSubmitting(false);
    setMinimized(false);
    onClose();
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      saveDraft({ formData, articleLines });
      onClose();
    }
  };

  return (
    <>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="lg" fullWidth TransitionComponent={SlideUpTransition}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Nouvelle entree de stock
          <DialogControls color="#2196F3" onMinimize={minimize} onClose={handleClose} />
        </DialogTitle>
        <DialogContent>
          {/* Date and BL Number */}
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="BL N°"
                  value={formData.bl_number}
                  onChange={(e) => setFormData({ ...formData, bl_number: e.target.value })}
                  fullWidth
                  required
                  placeholder="Entrez le numéro de BL"
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Article Lines Section */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Articles</Typography>
              <IconButton
                color="primary"
                size="small"
                onClick={handleAddArticleLine}
                sx={{ border: '2px solid', borderColor: 'primary.main' }}
              >
                <AddIcon />
              </IconButton>
            </Box>

            {articleLines.map((line, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={articles}
                      getOptionLabel={(option) => `${option.reference} - ${option.designation}`}
                      value={articles.find((a) => a.id === line.article_id) || null}
                      onChange={(_, value) => handleArticleChange(index, value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Article"
                          placeholder="Selectionner article"
                          size="small"
                          required
                        />
                      )}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={6} sm={3} md={3}>
                    <TextField
                      label="Quantite"
                      type="text"
                      value={line.quantity ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          handleArticleFieldChange(index, 'quantity', null);
                        } else if (/^\d*[.,]?\d*$/.test(value)) {
                          handleArticleFieldChange(index, 'quantity', value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value !== '') {
                          const numValue = parseFloat(value.replace(',', '.'));
                          if (!isNaN(numValue)) {
                            handleArticleFieldChange(index, 'quantity', numValue);
                          } else {
                            handleArticleFieldChange(index, 'quantity', null);
                          }
                        }
                      }}
                      size="small"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={3} md={3}>
                    <TextField
                      label="Prix Unitaire"
                      type="text"
                      value={line.unit_price ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          handleArticleFieldChange(index, 'unit_price', null);
                        } else if (/^\d*[.,]?\d*$/.test(value)) {
                          handleArticleFieldChange(index, 'unit_price', value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value !== '') {
                          const numValue = parseFloat(value.replace(',', '.'));
                          if (!isNaN(numValue)) {
                            handleArticleFieldChange(index, 'unit_price', numValue);
                          } else {
                            handleArticleFieldChange(index, 'unit_price', null);
                          }
                        }
                      }}
                      size="small"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm="auto" md="auto" sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleRemoveArticleLine(index)}
                      disabled={articleLines.length === 1}
                      sx={{
                        border: '1px solid',
                        borderColor: articleLines.length === 1 ? 'grey.300' : 'error.main',
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Supplier Field */}
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  options={suppliers}
                  getOptionLabel={(option) => `${option.code} - ${option.name}`}
                  value={suppliers.find((s) => s.id === formData.supplier_id) || null}
                  onChange={(_, value) => setFormData({ ...formData, supplier_id: value?.id || null })}
                  renderInput={(params) => <TextField {...params} label="Fournisseur" required />}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button onClick={handleSubmitClick} variant="contained">
            Creer
          </Button>
        </DialogActions>
      </Dialog>

      <MinimizedBar
        open={open && minimized}
        color="#2196F3"
        title="Nouvelle entree de stock"
        onRestore={restore}
        onClose={handleClose}
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={showConfirmDialog}
        title="Confirmer l'entree de stock"
        message="Veuillez verifier les informations avant de confirmer l'entree de stock."
        fields={getConfirmationFields()}
        movementType="entry"
        onConfirm={handleConfirmSubmit}
        onCancel={() => setShowConfirmDialog(false)}
        confirmText="Confirmer l'entree"
        cancelText="Annuler"
        loading={isSubmitting}
      />

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onClose={handleSuccessDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Entree de stock creee avec succes</DialogTitle>
        <DialogContent>
          {createdResponse && (
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>BL N:</strong> {createdResponse.bl_number}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Nombre d'articles:</strong> {createdResponse.total_articles}
              </Typography>
              <Typography variant="body1">
                <strong>Total:</strong> {formatNumber(createdResponse.grand_total)} MAD
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSuccessDialogClose}
            variant="contained"
            sx={{
              backgroundColor: '#2196F3',
              '&:hover': {
                backgroundColor: '#1976D2',
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StockEntryNewAdd;
